// src/api/orders.js
import {
  DynamoDBClient,
  QueryCommand,
  GetItemCommand,
  UpdateItemCommand,
  ScanCommand
} from '@aws-sdk/client-dynamodb';
import getAwsConfig from '../awsconfig';
import { updateItem, getItem, listItems } from './inventory';
import { createNotification } from './notifications';

const TABLE = 'ordersinventory';
let processingInterval = null;

async function makeClient() {
  const cfg = await getAwsConfig();
  return new DynamoDBClient(cfg);
}

function unmarshallOrder(item) {
  if (!item) return null;
  
  return {
    orderId: item.orderId.S,
    createdDate: item.createdDate.S,
    status: item.status?.S || 'PENDING',
    items: item.items?.L ? item.items.L.map(i => ({
      productId: i.M.productId?.S || '',
      productType: i.M.productType?.S || '',
      quantity: i.M.quantity?.N ? Number(i.M.quantity.N) : 0,
      name: i.M.name?.S || '',
      price: i.M.price?.N ? Number(i.M.price.N) : 0
    })) : [],
    customer: item.customer?.M ? {
      name: item.customer.M.name?.S || '',
      email: item.customer.M.email?.S || '',
      address: item.customer.M.address?.S || ''
    } : {},
    shipping: item.shipping?.M ? {
      method: item.shipping.M.method?.S || 'DOMESTIC',
      trackingNumber: item.shipping.M.trackingNumber?.S || ''
    } : { method: 'DOMESTIC' },
    processed: item.processed?.BOOL || false,
    processingDate: item.processingDate?.S || '',
    updatedAt: item.updatedAt?.S || ''
  };
}

/**
 * List all orders with a specific status
 */
export async function listOrdersByStatus(status = 'PENDING') {
  const db = await makeClient();
  
  const command = new QueryCommand({
    TableName: TABLE,
    IndexName: 'StatusIndex',
    KeyConditionExpression: 'status = :status',
    ExpressionAttributeValues: {
      ':status': { S: status }
    },
    ScanIndexForward: false // Most recent first
  });
  
  const result = await db.send(command);
  return (result.Items || []).map(unmarshallOrder);
}

/**
 * List all unprocessed orders
 */
export async function listUnprocessedOrders() {
  const db = await makeClient();
  
  const command = new ScanCommand({
    TableName: TABLE,
    FilterExpression: 'processed = :processed',
    ExpressionAttributeValues: {
      ':processed': { BOOL: false }
    }
  });
  
  const result = await db.send(command);
  return (result.Items || []).map(unmarshallOrder);
}

/**
 * Get a single order by ID
 */
export async function getOrder(orderId, createdDate) {
  const db = await makeClient();
  
  const command = new GetItemCommand({
    TableName: TABLE,
    Key: {
      orderId: { S: orderId },
      createdDate: { S: createdDate }
    }
  });
  
  const result = await db.send(command);
  return unmarshallOrder(result.Item);
}

/**
 * Process an order and update inventory
 */
export async function processOrder(orderId, createdDate) {
  // 1. Get the order
  const order = await getOrder(orderId, createdDate);
  if (!order) throw new Error('Order not found');
  if (order.processed) return { success: true, message: 'Order already processed' };
  
  // 2. Get the automation settings
  const settings = await getAutomationSettings();
  
  // 3. Process each item in the order
  const updates = [];
  
  for (const item of order.items) {
    try {
      const result = await processOrderItem(item, order.shipping.method, settings);
      updates.push({
        item: item.name || item.productId,
        success: true,
        updates: result
      });
    } catch (error) {
      updates.push({
        item: item.name || item.productId,
        success: false,
        error: error.message
      });
      
      // Create notification for errors
      if (settings.notifyOnLowStock) {
        await createNotification('system', {
          itemType: item.productType === 'SEED_PACK' ? 'PRODUCT-SEED' : 'PRODUCT-CLONE',
          itemId: item.productId,
          message: `Error processing order item: ${error.message}`,
          name: item.name || item.productId
        });
      }
    }
  }
  
  // 4. Mark the order as processed
  const db = await makeClient();
  const now = new Date().toISOString();
  
  await db.send(new UpdateItemCommand({
    TableName: TABLE,
    Key: {
      orderId: { S: orderId },
      createdDate: { S: createdDate }
    },
    UpdateExpression: 'SET processed = :processed, processingDate = :date, updatedAt = :updated',
    ExpressionAttributeValues: {
      ':processed': { BOOL: true },
      ':date': { S: now },
      ':updated': { S: now }
    }
  }));
  
  return {
    success: true,
    message: 'Order processed successfully',
    updates
  };
}

/**
 * Process a single order item and update inventory
 */
async function processOrderItem(item, shippingMethod, settings) {
  const { productId, productType, quantity } = item;
  const updates = [];
  
  // Find related inventory items
  const inventoryItems = await findRelatedInventoryItems(productId);
  if (inventoryItems.length === 0) {
    throw new Error(`No inventory items found for product ${productId}`);
  }
  
  // Process each inventory item based on product type
  for (const invItem of inventoryItems) {
    // Handle seeds
    if (productType === 'SEED_PACK' || invItem.itemType === 'PRODUCT-SEED') {
      // Each seed pack decreases unpacked seeds by the configured amount (default 10)
      const seedsPerPack = settings.seedsPerPack || 10;
      const unpackedUpdate = -seedsPerPack * quantity;
      const packagedUpdate = quantity;
      
      // Check if there are enough unpacked seeds
      if (invItem.quantity + unpackedUpdate < 0) {
        throw new Error(`Not enough unpacked seeds. Need ${Math.abs(unpackedUpdate)}, have ${invItem.quantity}`);
      }
      
      // Update the inventory item
      await updateItem(invItem.itemType, invItem.itemId, {
        quantity: invItem.quantity + unpackedUpdate,
        packagedCount: invItem.packagedCount + packagedUpdate
      });
      
      updates.push({
        item: invItem.name,
        type: 'SEED',
        unpackedChange: unpackedUpdate,
        packagedChange: packagedUpdate
      });
      
      // Update packaging materials
      await updatePackagingMaterials('SEED_PACK', quantity, shippingMethod, settings);
    }
    
    // Handle clones
    else if (productType === 'CLONE' || invItem.itemType === 'PRODUCT-CLONE') {
      // Each clone decreases ready clones by 1
      const readyUpdate = -quantity;
      
      // Check if there are enough ready clones
      if (invItem.packagedCount + readyUpdate < 0) {
        throw new Error(`Not enough ready clones. Need ${Math.abs(readyUpdate)}, have ${invItem.packagedCount}`);
      }
      
      // Update the inventory item
      await updateItem(invItem.itemType, invItem.itemId, {
        packagedCount: invItem.packagedCount + readyUpdate
      });
      
      updates.push({
        item: invItem.name,
        type: 'CLONE',
        readyChange: readyUpdate
      });
      
      // Update packaging materials
      await updatePackagingMaterials('CLONE', quantity, shippingMethod, settings);
    }
  }
  
  return updates;
}

/**
 * Find inventory items related to a product
 */
async function findRelatedInventoryItems(productId) {
  // First check for seeds
  let items = await listItems('PRODUCT-SEED');
  let filtered = items.filter(item => item.productRefId === productId);
  
  if (filtered.length > 0) return filtered;
  
  // Then check for clones
  items = await listItems('PRODUCT-CLONE');
  filtered = items.filter(item => item.productRefId === productId);
  
  return filtered;
}

/**
 * Update packaging materials based on order item
 */
async function updatePackagingMaterials(productType, quantity, shippingMethod, settings) {
  const materials = await listItems('SHIPPING_MATERIAL');
  const materialNames = settings.materialsMappings || {};
  
  try {
    // Process based on product type
    if (productType === 'SEED_PACK') {
      // For each seed pack:
      // - 1 bag (seed pack bag)
      // - 1 label (seed pack label)
      const bagName = materialNames.seedPack?.bag || 'Seed Pack Bag';
      const labelName = materialNames.seedPack?.label || 'Seed Pack Label';
      
      await updateMaterial(materials, bagName, -quantity);
      await updateMaterial(materials, labelName, -quantity);
      
      // Additional materials if configured
      if (materialNames.seedPack?.jar) {
        await updateMaterial(materials, materialNames.seedPack.jar, -quantity);
      }
      
      if (materialNames.seedPack?.pad) {
        await updateMaterial(materials, materialNames.seedPack.pad, -quantity);
      }
    }
    else if (productType === 'CLONE') {
      // For each clone:
      // - 1 LED plant shipper with label
      const shipperName = materialNames.clone?.shipper || 'LED Plant Shipper';
      await updateMaterial(materials, shipperName, -quantity);
      
      // Only use 1 large USPS box per order, regardless of quantity
      // This will be handled at the shipping level
    }
    
    // Process shipping materials (one per order, not per item)
    if (shippingMethod === 'DOMESTIC') {
      if (productType === 'SEED_PACK') {
        // Seeds ship in padded shipper with postage label
        const shipperName = materialNames.shipping?.domestic?.shipper || 'USPS Padded Shipper';
        const labelName = materialNames.shipping?.domestic?.label || 'Postage Label';
        
        await updateMaterial(materials, shipperName, -1);
        await updateMaterial(materials, labelName, -1);
      } else if (productType === 'CLONE') {
        // Clones ship in a large USPS box with postage label
        const boxName = materialNames.clone?.box || 'Large USPS Box';
        const labelName = materialNames.shipping?.domestic?.label || 'Postage Label';
        
        await updateMaterial(materials, boxName, -1);
        await updateMaterial(materials, labelName, -1);
      }
    } else if (shippingMethod === 'INTERNATIONAL') {
      // International orders use international shipper and label
      const shipperName = materialNames.shipping?.international?.shipper || 'International Padded Shipper';
      const labelName = materialNames.shipping?.international?.label || 'International Postage Label';
      
      await updateMaterial(materials, shipperName, -1);
      await updateMaterial(materials, labelName, -1);
    }
  } catch (error) {
    console.error(`Error updating packaging materials: ${error.message}`);
    throw new Error(`Failed to update packaging materials: ${error.message}`);
  }
}

/**
 * Update a specific packaging material
 */
async function updateMaterial(materials, materialName, quantityChange) {
  const material = materials.find(m => 
    m.name.toLowerCase().includes(materialName.toLowerCase())
  );
  
  if (!material) {
    throw new Error(`Packaging material "${materialName}" not found`);
  }
  
  // Check if we have enough stock
  if (material.quantity + quantityChange < 0) {
    throw new Error(`Not enough ${materialName}. Need ${Math.abs(quantityChange)}, have ${material.quantity}`);
  }
  
  await updateItem(
    'SHIPPING_MATERIAL',
    material.itemId,
    { quantity: material.quantity + quantityChange }
  );
}

/**
 * Get automation settings from the settings table
 */
async function getAutomationSettings() {
  // Import settings functionality
  const { getSettings } = require('../utils/settings');
  const settings = await getSettings();
  
  // Return automation settings with defaults
  return {
    seedsPerPack: settings.automation?.seedsPerPack || 10,
    notifyOnLowStock: settings.automation?.notifyOnLowStock !== false,
    enableAutoProcessing: settings.automation?.enableAutoProcessing || false,
    autoProcessingInterval: settings.automation?.autoProcessingInterval || 60,
    materialsMappings: settings.automation?.materialsMappings || {
      seedPack: {
        label: 'Seed Pack Label',
        bag: 'Seed Pack Bag',
        jar: 'Makeup Jar',
        pad: 'Diffuser Pad'
      },
      clone: {
        shipper: 'LED Plant Shipper',
        box: 'Large USPS Box'
      },
      shipping: {
        domestic: {
          shipper: 'USPS Padded Shipper',
          label: 'Postage Label'
        },
        international: {
          shipper: 'International Padded Shipper',
          label: 'International Postage Label'
        }
      }
    }
  };
}

/**
 * Start the automatic order data import background task
 */
export async function startAutoProcessing() {
  if (processingInterval) {
    console.log('Order data import already running');
    return;
  }

  const settings = await getAutomationSettings();
  if (!settings.enableAutoProcessing) {
    console.log('Order data import is disabled in settings');
    return;
  }

  const intervalMinutes = settings.autoProcessingInterval || 60;
  const intervalMs = intervalMinutes * 60 * 1000;

  console.log(`Starting order data import with interval: ${intervalMinutes} minutes`);
  
  // Run immediately once
  await runAutoProcessing();
  
  // Then set up the interval
  processingInterval = setInterval(runAutoProcessing, intervalMs);
}

/**
 * Stop the automatic order data import background task
 */
export function stopAutoProcessing() {
  if (processingInterval) {
    clearInterval(processingInterval);
    processingInterval = null;
    console.log('Order data import stopped');
  }
}

/**
 * Import all unprocessed orders and update inventory
 */
async function runAutoProcessing() {
  try {
    console.log('Running order data import task...');
    
    // Check if auto import is still enabled in settings
    const settings = await getAutomationSettings();
    if (!settings.enableAutoProcessing) {
      console.log('Order data import is now disabled in settings, stopping');
      stopAutoProcessing();
      return;
    }
    
    // Get unprocessed orders
    const orders = await listUnprocessedOrders();
    console.log(`Found ${orders.length} unprocessed orders for inventory updates`);
    
    // Process each order's data for inventory
    for (const order of orders) {
      try {
        console.log(`Importing order data: ${order.orderId}`);
        await processOrder(order.orderId, order.createdDate);
        console.log(`Order data imported successfully: ${order.orderId}`);
      } catch (error) {
        console.error(`Error importing order data ${order.orderId}:`, error);
        
        // Create notification for error
        await createNotification('system', {
          itemType: 'ERROR',
          itemId: order.orderId,
          message: `Failed to automatically import order ${order.orderId}: ${error.message}`,
          name: 'Order Data Import Error'
        });
      }
    }
    
    console.log('Order data import task completed');
  } catch (error) {
    console.error('Error in order data import task:', error);
  }
} 